/**
 * 결함 유형(상위·하위) 프론트 카탈로그.
 * - 배열에 상위 항목을 하나 이상 넣으면(isDefectTypesCatalogActive) 결함 타입 관련 HTTP는 나가지 않는다.
 *   - 상·하위 목록·단건·설명·전체 목록은 전부 이 파일의 데이터만 사용한다.
 * - id·seq는 운영 DB defect_type.seq 와 반드시 일치해야 PUT /module-defects 시 defectCode 가 유효하다.
 * - 상위·하위 모두 선택 필드: description, powerLossRate, viewOrder 등 (서버 엔티티와 맞추면 폼·팝업에 그대로 반영)
 *
 * 예시 (실제 seq·문구로 교체 후 사용):
 *
 * export const DEFECT_TYPES_CATALOG = [
 *   {
 *     id: 2,
 *     seq: 2,
 *     code: 'HOT_SPOT',
 *     name: '핫스팟',
 *     description: '상위 유형 설명(선택)',
 *     viewOrder: 1,
 *     children: [
 *       {
 *         id: 15,
 *         seq: 15,
 *         code: 'HOT_SPOT_MEDIUM',
 *         name: '중간 핫스팟',
 *         description: '하위 유형 설명(선택)'
 *       }
 *     ]
 *   }
 * ];
 */

export const DEFECT_TYPES_CATALOG = [];

export function isDefectTypesCatalogActive() {
  return (
    Array.isArray(DEFECT_TYPES_CATALOG) && DEFECT_TYPES_CATALOG.length > 0
  );
}

export function getCatalogParentDefectTypes() {
  return DEFECT_TYPES_CATALOG.map((p) => {
    const { children: _c, ...rest } = p;
    const id = rest.id != null ? Number(rest.id) : Number(rest.seq);
    return {
      ...rest,
      id,
      seq: rest.seq != null ? Number(rest.seq) : id,
      parent: null
    };
  }).sort((a, b) => (a.viewOrder ?? 0) - (b.viewOrder ?? 0));
}

export function getCatalogChildrenForParent(parentKey) {
  const k = Number(parentKey);
  if (!Number.isFinite(k)) {
    return [];
  }
  const parent = DEFECT_TYPES_CATALOG.find((p) => {
    const pid = p.id != null ? Number(p.id) : Number(p.seq);
    return pid === k;
  });
  if (!parent || !Array.isArray(parent.children)) {
    return [];
  }
  const parentId = parent.id != null ? Number(parent.id) : Number(parent.seq);
  return parent.children.map((ch) => {
    const cid = ch.id != null ? Number(ch.id) : Number(ch.seq);
    return {
      ...ch,
      id: cid,
      seq: ch.seq != null ? Number(ch.seq) : cid,
      parent: { id: parentId }
    };
  });
}

/**
 * 카탈로그에서 id/seq 로 상위 또는 하위 결함 타입 한 건 (GET /defectTypes/{id} 대체용).
 * @param {string|number} defectTypeId
 * @returns {object|null} id, seq, code, name, description?, parent|null|{id}
 */
export function getCatalogDefectTypeById(defectTypeId) {
  const target = Number(defectTypeId);
  if (!Number.isFinite(target) || target <= 0) {
    return null;
  }
  for (const p of DEFECT_TYPES_CATALOG) {
    const pid = p.id != null ? Number(p.id) : Number(p.seq);
    if (pid === target) {
      const { children: _c, ...rest } = p;
      const id = rest.id != null ? Number(rest.id) : Number(rest.seq);
      return {
        ...rest,
        id,
        seq: rest.seq != null ? Number(rest.seq) : id,
        parent: null
      };
    }
    if (Array.isArray(p.children)) {
      for (const ch of p.children) {
        const cid = ch.id != null ? Number(ch.id) : Number(ch.seq);
        if (cid === target) {
          return {
            ...ch,
            id: cid,
            seq: ch.seq != null ? Number(ch.seq) : cid,
            parent: { id: pid }
          };
        }
      }
    }
  }
  return null;
}

/**
 * WFS defect_type 문자열을 카탈로그의 상위/하위 객체로만 해석 (API 순회 없음).
 * @param {string} wfsCodeRaw
 * @returns {{ mainType: object, subType: object|null, subDefectTypes: object[] }|null}
 */
/** 상위+하위 전부 한 배열 (레거시 GET /defectTypes 대체용) */
export function getCatalogAllDefectTypesFlat() {
  const flat = [];
  for (const p of DEFECT_TYPES_CATALOG) {
    const pid = p.id != null ? Number(p.id) : Number(p.seq);
    const { children: _chs, ...pRest } = p;
    const mid = pRest.id != null ? Number(pRest.id) : Number(pRest.seq);
    flat.push({
      ...pRest,
      id: mid,
      seq: pRest.seq != null ? Number(pRest.seq) : mid,
      parent: null
    });
    flat.push(...getCatalogChildrenForParent(pid));
  }
  return flat;
}

export function resolveCatalogWfsDefectSelection(wfsCodeRaw) {
  const wfsCode = String(wfsCodeRaw || '').trim();
  if (!wfsCode || !DEFECT_TYPES_CATALOG.length) {
    return null;
  }
  const codeEq = (a, b) =>
    a != null &&
    b != null &&
    String(a).toUpperCase() === String(b).toUpperCase();

  for (const p of DEFECT_TYPES_CATALOG) {
    const pid = p.id != null ? Number(p.id) : Number(p.seq);
    const { children: chs, ...pRest } = p;
    const mid = pRest.id != null ? Number(pRest.id) : Number(pRest.seq);
    const mainType = {
      ...pRest,
      id: mid,
      seq: pRest.seq != null ? Number(pRest.seq) : mid,
      parent: null
    };
    const subDefectTypes = getCatalogChildrenForParent(pid);

    if (codeEq(p.code, wfsCode)) {
      return { mainType, subType: null, subDefectTypes };
    }
    if (Array.isArray(chs)) {
      const subHit = chs.find((c) => codeEq(c.code, wfsCode));
      if (subHit) {
        const cid = subHit.id != null ? Number(subHit.id) : Number(subHit.seq);
        const subType = {
          ...subHit,
          id: cid,
          seq: subHit.seq != null ? Number(subHit.seq) : cid,
          parent: { id: pid }
        };
        return { mainType, subType, subDefectTypes };
      }
    }
  }
  return null;
}
